import { metrics } from '@sentry/core';
import * as Sentry from '@sentry/react';
import type { ConfigurationResponse } from '../types';
import { AttributeValue, getMetricAttributes } from './getMetricAttributes';

/**
 * Records how long a user-facing flow took, as a millisecond distribution carrying the
 * shared metric context.
 *
 * Record every request that completed, and carry how it ended as an attribute rather than
 * deciding not to record it. Skipping is only right when the flow genuinely did not run:
 * a request that failed or whose result never reached the user is still a duration someone
 * waited, and dropping it censors the distribution — usually at the slow end, which is the
 * end worth measuring.
 *
 * Sentry aggregates the metric value only — attributes are filter and group-by dimensions,
 * so anything that needs its own percentile has to be its own data point, not an attribute.
 * Keep them low-cardinality: there is a 2KB per-metric limit, above which Sentry silently
 * drops the metric.
 *
 */
export const captureTimingMetric = (
  name: string,
  durationMs: number,
  config?: ConfigurationResponse['data'],
  extraAttributes?: Record<string, AttributeValue>,
): void => {
  metrics.distribution(name, Math.round(durationMs), {
    unit: 'millisecond',
    attributes: { ...getMetricAttributes(config), ...extraAttributes },
  });

  // Metrics buffer for up to 5 seconds before being sent. These are recorded at the end of
  // a flow, exactly when the user may dismiss the task pane, so flush rather than lose them.
  // Not awaited: the flow is already finished and nothing downstream depends on delivery.
  Sentry.flush(2000).catch(() => {});
};
